import { parseAppHandoff, stripAppHandoffHash } from './app-handoff'

export const AUTH_ACCESS_KEY = 'auth_access_token'
export const AUTH_EXPIRES_KEY = 'auth_expires_at'
/** Cross-tab lock while POST /auth/refresh is in flight (same browser profile). */
export const AUTH_REFRESH_LOCK_KEY = 'auth_refresh_lock'

/** Keys that affect session when changed in another tab or DevTools. */
export const AUTH_SYNC_STORAGE_KEYS = [
  AUTH_ACCESS_KEY,
  AUTH_EXPIRES_KEY,
  'auth_refresh_token',
  'auth_token',
] as const

const ACCESS_KEY = AUTH_ACCESS_KEY
const REFRESH_KEY = 'auth_refresh_token'
const EXPIRES_KEY = AUTH_EXPIRES_KEY
const LEGACY_TOKEN_KEY = 'auth_token'
const REMEMBER_KEY = 'auth_remember'
const REMEMBER_EMAIL_KEY = 'auth_remember_email'
/** Set while logging out — blocks cookie refresh until next login. */
export const AUTH_LOGOUT_PENDING_KEY = 'auth_logout_pending'

export type AuthTokens = {
  access_token: string
  expires_in: number
}

function storageFor(remember: boolean): Storage {
  return remember ? localStorage : sessionStorage
}

function readFromBoth(key: string): string | null {
  return sessionStorage.getItem(key) || localStorage.getItem(key)
}

function migrateLegacyToken(): void {
  const legacy = readFromBoth(LEGACY_TOKEN_KEY)
  if (legacy && !readFromBoth(ACCESS_KEY)) {
    localStorage.setItem(ACCESS_KEY, legacy)
    if (!readFromBoth(EXPIRES_KEY)) {
      localStorage.setItem(EXPIRES_KEY, String(Date.now() + 3600 * 1000))
    }
  }
  localStorage.removeItem(LEGACY_TOKEN_KEY)
  sessionStorage.removeItem(LEGACY_TOKEN_KEY)
  clearStoredRefreshToken()
}

migrateLegacyToken()

export function clearStoredRefreshToken(): void {
  localStorage.removeItem(REFRESH_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
}

export function isRememberLogin(): boolean {
  return localStorage.getItem(REMEMBER_KEY) !== '0'
}

/** Last login identifier (email or username) when "remember me" is on. */
export function getRememberedLogin(): string {
  if (!isRememberLogin()) return ''
  return localStorage.getItem(REMEMBER_EMAIL_KEY) ?? ''
}

/** @deprecated Use getRememberedLogin */
export function getRememberedEmail(): string {
  return getRememberedLogin()
}

export function getAccessToken(): string | null {
  return readFromBoth(ACCESS_KEY)
}

export function getAccessExpiresAt(): number | null {
  const raw = readFromBoth(EXPIRES_KEY)
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** Refresh access token this many ms before it expires. */
export const ACCESS_TOKEN_REFRESH_BUFFER_MS = 60_000

export function isAccessTokenExpired(): boolean {
  const expiresAt = getAccessExpiresAt()
  if (!expiresAt) return false
  return Date.now() >= expiresAt
}

export function shouldRefreshAccessToken(): boolean {
  const token = getAccessToken()
  if (!token) return false

  const expiresAt = getAccessExpiresAt()
  // Legacy sessions without expiry metadata — refresh proactively before API calls.
  if (!expiresAt) return true

  return Date.now() >= expiresAt - ACCESS_TOKEN_REFRESH_BUFFER_MS
}

export function setAuthTokens(tokens: AuthTokens, remember: boolean): void {
  // Overwrite in place — never clearAccess first (other tabs see a brief null and log out).
  localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0')

  const storage = storageFor(remember)
  const other = remember ? sessionStorage : localStorage
  for (const key of [ACCESS_KEY, EXPIRES_KEY, REFRESH_KEY, LEGACY_TOKEN_KEY]) {
    other.removeItem(key)
  }

  const ttlSec = Number(tokens.expires_in)
  const expiresIn = Number.isFinite(ttlSec) && ttlSec > 0 ? ttlSec : 3600
  storage.setItem(ACCESS_KEY, tokens.access_token)
  storage.setItem(EXPIRES_KEY, String(Date.now() + expiresIn * 1000))
  clearStoredRefreshToken()
}

export function setRememberedLogin(login: string, remember: boolean): void {
  if (remember) {
    localStorage.setItem(REMEMBER_EMAIL_KEY, login.trim())
  } else {
    localStorage.removeItem(REMEMBER_EMAIL_KEY)
  }
}

/** @deprecated Use setRememberedLogin */
export function setRememberedEmail(login: string, remember: boolean): void {
  setRememberedLogin(login, remember)
}

export function clearAuthTokens(): void {
  for (const key of [ACCESS_KEY, REFRESH_KEY, EXPIRES_KEY, LEGACY_TOKEN_KEY]) {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  }
}

export function markLogoutPending(): void {
  sessionStorage.setItem(AUTH_LOGOUT_PENDING_KEY, '1')
}

export function clearLogoutPending(): void {
  sessionStorage.removeItem(AUTH_LOGOUT_PENDING_KEY)
}

export function isLogoutPending(): boolean {
  return sessionStorage.getItem(AUTH_LOGOUT_PENDING_KEY) === '1'
}

/** @deprecated Use getAccessToken */
export function getAuthToken(): string | null {
  return getAccessToken()
}

/** @deprecated Use setAuthTokens */
export function setAuthToken(token: string | null, remember = true): void {
  if (!token) {
    clearAuthTokens()
    localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0')
    return
  }
  setAuthTokens({ access_token: token, expires_in: 3600 }, remember)
}

/** Adopt the staff-app session before Vue router guards run. */
function consumeAppHandoffFromLocation(): void {
  const handoff = parseAppHandoff()
  if (!handoff) return
  setAuthTokens(handoff, true)
  stripAppHandoffHash()
}

consumeAppHandoffFromLocation()
